import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { requestPasswordReset } from '../../services/api';
import { getAndClearIntendedRoute, saveIntendedRoute } from '../../utils/routeUtils';

interface LoginFormProps {
  onSwitchToRegister: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onSwitchToRegister }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showResetForm, setShowResetForm] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [isResetting, setIsResetting] = useState(false);
  const [showResendVerification, setShowResendVerification] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const [isResending, setIsResending] = useState(false);
  const { login, isLoading, error } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Check for redirect query param and store it
  useEffect(() => {
    const redirect = searchParams.get('redirect');
    if (redirect) {
      saveIntendedRoute(redirect);
    }
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await login(email, password);
      
      // After successful login, redirect to intended route or default route
      const intendedRoute = getAndClearIntendedRoute();
      if (intendedRoute) {
        navigate(intendedRoute, { replace: true });
      } else {
        // Get default route based on user role (will be available after login)
        navigate('/dashboard', { replace: true });
      }
    } catch (err) {
      // Error is handled by the auth context
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsResetting(true);
    setResetMessage(null);
    
    try {
      const response = await requestPasswordReset(resetEmail);
      if (response.success) {
        setResetMessage({ 
          type: 'success', 
          text: 'Password reset instructions have been sent to your email address.' 
        });
        setResetEmail('');
        setTimeout(() => {
          setShowResetForm(false);
          setResetMessage(null);
        }, 3000);
      } else {
        setResetMessage({ 
          type: 'error', 
          text: typeof response.error === 'string' ? response.error : 'Failed to send reset instructions.' 
        });
      }
    } catch (err: any) {
      setResetMessage({ 
        type: 'error', 
        text: err.message || 'Failed to send reset instructions.' 
      });
    } finally {
      setIsResetting(false);
    }
  };

  const handleResendVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsResending(true);
    setResetMessage(null);
    
    try {
      const response = await fetch('/api/v1/email/resend-verification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: resendEmail }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        setResetMessage({ 
          type: 'success', 
          text: data.data.message || 'A new verification email has been sent to your email address.' 
        });
        setResendEmail('');
        setTimeout(() => {
          setShowResendVerification(false);
          setResetMessage(null);
        }, 3000);
      } else {
        setResetMessage({ 
          type: 'error', 
          text: data.error || 'Failed to send verification email.' 
        });
      }
    } catch (err: any) {
      setResetMessage({ 
        type: 'error', 
        text: err.message || 'Failed to send verification email.' 
      });
    } finally {
      setIsResending(false);
    }
  };

  if (showResetForm) {
    return (
      <div className="card max-w-md mx-auto">
        <h2 className="text-2xl font-bold mb-6 text-center">Reset Password</h2>
        
        <form onSubmit={handlePasswordReset} className="space-y-4">
          <div>
            <label htmlFor="reset-email" className="block text-sm font-medium mb-2">
              Email Address
            </label>
            <input
              id="reset-email"
              type="email"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              className="input"
              placeholder="Enter your email address"
              required
            />
          </div>
          
          {resetMessage && (
            <div className={`text-sm rounded-lg p-3 ${
              resetMessage.type === 'success' 
                ? 'text-green-400 bg-green-500/10 border border-green-500/20' 
                : 'text-red-400 bg-red-500/10 border border-red-500/20'
            }`}>
              {resetMessage.text}
            </div>
          )}
          
          <button
            type="submit"
            disabled={isResetting}
            className="btn primary w-full"
          >
            {isResetting ? 'Sending...' : 'Send Reset Instructions'}
          </button>
        </form>
        
        <div className="mt-6 text-center">
          <button
            onClick={() => {
              setShowResetForm(false);
              setResetMessage(null);
              setResetEmail('');
            }}
            className="text-cyan-400 hover:text-cyan-300 font-medium text-sm"
          >
            ← Back to Login
          </button>
        </div>
      </div>
    );
  }

  if (showResendVerification) {
    return (
      <div className="card max-w-md mx-auto">
        <h2 className="text-2xl font-bold mb-6 text-center">Resend Verification Email</h2>
        
        <form onSubmit={handleResendVerification} className="space-y-4">
          <div>
            <label htmlFor="resendEmail" className="block text-sm font-medium mb-2">
              Email Address
            </label>
            <input
              id="resendEmail"
              type="email"
              value={resendEmail}
              onChange={(e) => setResendEmail(e.target.value)}
              className="input"
              placeholder="Enter your email address"
              required
            />
          </div>
          
          {resetMessage && (
            <div className={`p-3 rounded-md text-sm ${
              resetMessage.type === 'success' 
                ? 'bg-green-100 text-green-800 border border-green-200' 
                : 'bg-red-100 text-red-800 border border-red-200'
            }`}>
              {resetMessage.text}
            </div>
          )}
          
          <button
            type="submit"
            disabled={isResending}
            className="btn primary w-full"
          >
            {isResending ? 'Sending...' : 'Send Verification Email'}
          </button>
        </form>
        
        <div className="mt-6 text-center">
          <button
            onClick={() => {
              setShowResendVerification(false);
              setResetMessage(null);
              setResendEmail('');
            }}
            className="text-cyan-400 hover:text-cyan-300 font-medium text-sm"
          >
            ← Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="card max-w-md mx-auto">
      <h2 className="text-2xl font-bold mb-6 text-center">Welcome Back</h2>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="email" className="block text-sm font-medium mb-2">
            Email
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="input"
            placeholder="Enter your email"
            required
          />
        </div>
        
        <div>
          <label htmlFor="password" className="block text-sm font-medium mb-2">
            Password
          </label>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input pr-10"
              placeholder="Enter your password"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-300"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              )}
            </button>
          </div>
        </div>
        
        {error && (
          <div className="text-red-500 text-sm bg-red-500/10 border border-red-500/20 rounded-lg p-3">
            {error}
          </div>
        )}
        
        <button
          type="submit"
          disabled={isLoading}
          className="btn primary w-full"
        >
          {isLoading ? 'Signing in...' : 'Sign In'}
        </button>
      </form>
      
      <div className="mt-4 text-center space-y-2">
        <button
          onClick={() => setShowResetForm(true)}
          className="text-cyan-400 hover:text-cyan-300 font-medium text-sm block"
        >
          Forgot your password?
        </button>
        <button
          onClick={() => setShowResendVerification(true)}
          className="text-cyan-400 hover:text-cyan-300 font-medium text-sm block"
        >
          Resend verification email?
        </button>
      </div>
      
      <div className="mt-6 text-center">
        <p className="text-sm text-gray-400">
          Don't have an account?{' '}
          <button
            onClick={onSwitchToRegister}
            className="text-cyan-400 hover:text-cyan-300 font-medium"
          >
            Sign up
          </button>
        </p>
      </div>
    </div>
  );
};

export default LoginForm;
